/**
 * ReportComposerShell — M6 Output Factory · Increment 1 (2026-06-25, Anil).
 *
 * A STAFF surface that stands up the report PHASE / VERSION structure +
 * tier-gating around the EXISTING report engine. Increment 1 = SCAFFOLDING ONLY:
 *   - lists the project's report PHASES (Project → Report(phase) → Version);
 *   - shows the VERSION history per phase (v1 · v2 · v3 + status);
 *   - "＋ New report phase" (named) — L2/L3 only (tier-gated);
 *   - "Generate version" wires the EXISTING `composeReport` engine (reportDelivery.ts) —
 *     report CONTENT/engine output is NOT changed here (that's Increment 3).
 *
 * Tier gating (mockup §3 callout + master map §3): L1 = single LOCKED auto report
 * (no phases, no Vastu floor plan); L2 = curated (may add a phase); L3 = open.
 *
 * The pick/drag-from-Findings-Pool composer + new-intake-data consumption are
 * Increments 2 & 3 — NOT here.
 *
 * Injectable seam (so the no-Firebase preview harness can drive it with mock
 * data): `reportsOverride` supplies the report docs; `onGenerate` overrides the
 * compose call. In production both are omitted → it streams live reports +
 * calls the real `composeReport`.
 */
import { useEffect, useMemo, useState } from 'react';
import { Box, Btn3, Tag } from './ui';
import type { Role } from './../lib/roles';
import {
  composeReport,
  canGenerateReport,
  toDeliverableTier,
  listenProjectReports,
  type ReportDoc,
} from '../lib/reportDelivery';
import {
  phaseModelForTier,
  groupReportsIntoPhases,
  nextPhaseNo,
  makePhaseDef,
  tierLevel,
  type ReportPhase,
  type ReportPhaseDef,
} from '../lib/reportPhases';
import { listenReportPhaseDefs, saveReportPhaseDef } from '../lib/reportPhasesStore';

export interface ReportComposerShellProps {
  clientId: string | null;
  projectId: string;
  tierCode: string;
  role: Role | null;
  actorUid?: string | null;
  onMsg?: (m: string) => void;
  /** PREVIEW seam — supply report docs instead of streaming Firebase. */
  reportsOverride?: ReportDoc[];
  /** PREVIEW seam — supply phase definitions instead of streaming Firebase. */
  phaseDefsOverride?: ReportPhaseDef[];
  /** PREVIEW seam — override the generate call (no real CF). */
  onGenerate?: (phase: ReportPhase | null) => Promise<void> | void;
}

const STATUS_TAG: Record<string, 'val' | 'warn' | 'default'> = {
  delivered: 'val',
  approved: 'val',
  draft: 'warn',
};

export default function ReportComposerShell({
  clientId,
  projectId,
  tierCode,
  role,
  actorUid,
  onMsg,
  reportsOverride,
  phaseDefsOverride,
  onGenerate,
}: ReportComposerShellProps) {
  const model = useMemo(() => phaseModelForTier(tierCode), [tierCode]);
  const level = tierLevel(tierCode);
  const deliverableTier = toDeliverableTier(tierCode);
  const mayGenerate = canGenerateReport(role);

  // Report docs: live stream OR injected preview override.
  const [liveReports, setLiveReports] = useState<ReportDoc[]>([]);
  useEffect(() => {
    if (reportsOverride) return; // preview mode — no Firebase
    if (!clientId || !deliverableTier) {
      setLiveReports([]);
      return;
    }
    const unsub = listenProjectReports(clientId, projectId, setLiveReports, () => {});
    return unsub;
  }, [reportsOverride, clientId, projectId, deliverableTier]);
  const reports = reportsOverride ?? liveReports;

  // Staff-defined phases — Increment 2: PERSISTED to Firestore
  // (clients/{cid}/reportPhases). The named phase shows immediately (optimistic)
  // and survives reload. Preview mode injects `phaseDefsOverride` (no Firebase).
  const [livePhaseDefs, setLivePhaseDefs] = useState<ReportPhaseDef[]>([]);
  useEffect(() => {
    if (phaseDefsOverride) return; // preview mode
    if (!clientId || !projectId) { setLivePhaseDefs([]); return; }
    const unsub = listenReportPhaseDefs(clientId, projectId, setLivePhaseDefs, () => {});
    return unsub;
  }, [phaseDefsOverride, clientId, projectId]);
  // Optimistic local additions merged over the streamed defs (de-duped by phaseId).
  const [pendingDefs, setPendingDefs] = useState<ReportPhaseDef[]>([]);
  const phaseDefs = useMemo(() => {
    const base = phaseDefsOverride ?? livePhaseDefs;
    const seen = new Set(base.map((d) => d.phaseId));
    return [...base, ...pendingDefs.filter((d) => !seen.has(d.phaseId))];
  }, [phaseDefsOverride, livePhaseDefs, pendingDefs]);
  const phases = useMemo(
    () => groupReportsIntoPhases(reports, phaseDefs),
    [reports, phaseDefs],
  );

  // "＋ New report phase" inline form.
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [busy, setBusy] = useState<null | 'generate'>(null);

  function msg(m: string) { onMsg?.(m); }

  async function addPhase() {
    const no = nextPhaseNo(phases);
    const def = makePhaseDef(no, newLabel, new Date().toISOString());
    setPendingDefs((d) => [...d, def]); // optimistic
    setAdding(false);
    setNewLabel('');
    msg(`New report phase added: "${def.label}". Generate its first version below.`);
    // Persist (skip in preview / when no client link).
    if (!phaseDefsOverride && clientId) {
      try { await saveReportPhaseDef(clientId, projectId, def, actorUid || 'staff'); }
      catch (e) { msg(`Phase saved locally — could not persist: ${(e as Error).message}`); }
    }
  }

  async function generateVersion(phase: ReportPhase | null) {
    if (busy) return;
    if (onGenerate) { await onGenerate(phase); return; } // preview seam
    if (!clientId) { msg('No client linked to this project.'); return; }
    if (!deliverableTier) { msg(`The ${tierCode} report engine is not wired yet (Increment 3).`); return; }
    setBusy('generate');
    try {
      // M6 Inc-3 — pass the phase so the CF keys the version WITHIN it. When the
      // building is single-design (one implicit Default phase) phase is null →
      // legacy flat behaviour. A named phase (L2/L3) mints its own version line.
      const phasePart = phase && phase.phaseNo > 1
        ? { phaseNo: phase.phaseNo, phaseId: phase.phaseId, phaseLabel: phase.label }
        : {};
      const res = await composeReport({ clientId, projectId, tier: deliverableTier, forceRegen: true, ...phasePart });
      msg(`${deliverableTier} report generated (v${res.version_n}). Review, then approve & deliver.`);
    } catch (e) {
      const m = (e as Error).message || '';
      msg(/no intake|intake.*missing/i.test(m)
        ? 'Cannot generate yet — no floor data saved (Technical Intake panel first).'
        : `Generate failed: ${m}`);
    } finally {
      setBusy(null);
    }
  }

  const tierTagKind = level === 1 ? 'warn' : 'val';

  return (
    <Box
      title="Report Composer"
      note={`Project → Report phase → Version · ${tierCode || 'tier'} · ${model.label}`}
      allowOverflow
    >
      {/* Tier-behaviour banner */}
      <div
        style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap',
          border: '1px solid var(--accent-line, #d3e0ec)', background: 'var(--accent-wash, #eef3f8)',
          borderRadius: 'var(--r-sm)', padding: '10px 13px', marginBottom: 12,
        }}
      >
        <Tag kind={tierTagKind}>{`L${level || '?'} · ${model.label}`}</Tag>
        <div style={{ flex: 1, minWidth: 200, fontSize: 12.5, color: 'var(--accent-deep, #274b63)' }}>
          {model.note}
          {!model.draftsFloorPlan && (
            <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--warn-deep, #9a6a12)' }}>
              ⚠ No Vastu floor plan is drafted at this tier — assessment / report only.
            </div>
          )}
        </div>
      </div>

      {/* Phase strip */}
      <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--accent-deep, #274b63)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>
        Report phases &amp; versions
      </div>

      {phases.length === 0 && (
        <p style={{ fontSize: 12.5, color: 'var(--ink-mute)', margin: '0 0 10px' }}>
          No report generated yet. {model.kind === 'l1-locked'
            ? 'Generate the single locked assessment report below.'
            : 'Generate the first version, or add a named report phase.'}
        </p>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'stretch' }}>
        {phases.map((ph, i) => {
          const isLatestPhase = i === phases.length - 1;
          return (
            <div
              key={ph.phaseId}
              style={{
                border: `1px solid ${isLatestPhase ? 'var(--accent-line, #d3e0ec)' : 'var(--rule)'}`,
                background: isLatestPhase ? 'var(--accent-wash, #eef3f8)' : 'var(--surface)',
                borderRadius: 'var(--r-sm)', padding: '10px 12px', minWidth: 200, flex: '1 1 220px',
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--ink-mute)' }}>R{ph.phaseNo}</span>
                {ph.label}
              </div>
              <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {ph.versions.length === 0 ? (
                  <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>not generated yet</span>
                ) : (
                  ph.versions.slice().reverse().map((v) => (
                    <span
                      key={v.versionN}
                      style={{
                        fontFamily: 'monospace', fontSize: 11.5,
                        fontWeight: v.versionN === ph.latest?.versionN ? 700 : 400,
                        color: v.versionN === ph.latest?.versionN ? 'var(--ink)' : 'var(--ink-mute)',
                      }}
                      title={`${v.status || 'draft'}`}
                    >
                      v{v.versionN}
                    </span>
                  ))
                )}
                {ph.latest?.status && (
                  <Tag kind={STATUS_TAG[ph.latest.status] || 'default'}>{ph.latest.status}</Tag>
                )}
              </div>
              {mayGenerate && (
                <div style={{ marginTop: 9 }}>
                  <Btn3 intent="ghost" size="sm" onClick={() => generateVersion(ph)} disabled={busy === 'generate'}>
                    {busy === 'generate' ? 'Generating…' : ph.versions.length ? '+ New version' : 'Generate'}
                  </Btn3>
                </div>
              )}
            </div>
          );
        })}

        {/* ＋ New report phase — L2/L3 only. */}
        {model.canAddPhase && mayGenerate && (
          <div style={{ alignSelf: 'center' }}>
            {adding ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, border: '1px dashed var(--rule-strong)', borderRadius: 'var(--r-sm)', padding: 12, minWidth: 220 }}>
                <input
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder={`Report ${nextPhaseNo(phases)} name (e.g. Final plan)`}
                  style={{ border: '1px solid var(--rule-strong)', borderRadius: 'var(--r-sm)', padding: '7px 9px', fontSize: 12.5 }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <Btn3 intent="grn" size="sm" onClick={addPhase}>Add phase</Btn3>
                  <Btn3 intent="ghost" size="sm" onClick={() => { setAdding(false); setNewLabel(''); }}>Cancel</Btn3>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setAdding(true)}
                style={{ border: '1px dashed var(--rule-strong)', background: 'var(--surface)', borderRadius: 'var(--r-sm)', padding: '10px 14px', fontSize: 12.5, color: 'var(--link)', cursor: 'pointer', fontWeight: 600 }}
              >
                ＋ New report phase
              </button>
            )}
          </div>
        )}
      </div>

      {/* L1 locked note */}
      {model.kind === 'l1-locked' && (
        <div style={{ marginTop: 10, fontSize: 11.5, color: 'var(--ink-mute)' }}>
          L1 is a single, locked, auto-generated assessment report — no phases, no per-client structure (speed &amp; consistency over bespoke depth).
        </div>
      )}

      {/* Increment scope note */}
      <div style={{ marginTop: 12, fontSize: 11, color: 'var(--ink-mute)', borderTop: '1px solid var(--rule)', paddingTop: 8 }}>
        <b>Increment 1 (scaffolding):</b> phase/version structure + tier gating around the existing report engine.
        The pick/drag Findings-Pool composer and new-intake-data consumption are Increments 2 &amp; 3.
        {!deliverableTier && ` (The ${tierCode} engine is not yet wired for generation — only L1A/L2B today.)`}
      </div>
    </Box>
  );
}
